import type {
  Question,
  QuestionOption,
  QuestionAnswer,
  QuestionTree,
  QuestionType
} from "@/types";
import { validate as validateTosQuestion } from "../components/questions/tos-question";
import { validate as validateFileQuestion } from "../components/questions/file-question";
import { validate as validateLoginQuestion } from "../components/questions/login-question";
import { validate as validateEssayQuestion } from "../components/questions/essay-question";
import { validate as validateEmailQuestion } from "../components/questions/email-question";
import { validate as validateRegionQuestion } from "../components/questions/region-question";
import { validate as validateNumberQuestion } from "../components/questions/number-question";
import { validate as validatePasswordQuestion } from "../components/questions/password-question";
import { validate as validateCheckBoxQuestion } from "../components/questions/checkbox-question";
import { validate as validateDropDownQuestion } from "../components/questions/drop-down-question";
import { validate as validateSingleLineQuestion } from "../components/questions/single-line-question";
import { validate as validateRadioButtonQuestion } from "../components/questions/radio-button-question";
import { validate as validateAccountEmailQuestion } from "../components/questions/account-email-question";
import { validate as validateEmailOrScreenNameQuestion } from "../components/questions/email-or-screen-name-question";
import { validate as validateLoginPasswordQuestion } from "../components/questions/login-password-question";
import { validate as validateLocationQuestion } from "../components/questions/location-question";
import { validate as validateDateAgeQuestion } from "../components/questions/date-age-question";

type DataMemo = {
  questions: { [key: string]: Question };
  options: { [key: string]: QuestionOption };
};

type NodeTypeMemo = {
  rootNodes: Question[];
  childNodes: { [key: string]: Question[] };
};

const reduceQuestionsToDataMemo = (questions: Question[]): DataMemo => {
  return questions.reduce(
    (memo: DataMemo, question: Question) => {
      memo.questions[question.id] = question;
      if (question.questionOptions) {
        question.questionOptions.forEach((option) => {
          memo.options[option.id] = option;
        });
      }
      return memo;
    },
    {
      questions: {},
      options: {}
    }
  );
};

const isConditionalQuestion = (question: Question, memo: DataMemo): boolean => {
  const { hasTrigger, triggerQuestionId, triggerOptionId } = question;

  if (!hasTrigger || !triggerQuestionId || !triggerOptionId) {
    return false;
  }

  const { questions, options } = memo;

  return (
    Object.prototype.hasOwnProperty.call(questions, `${triggerQuestionId}`) &&
    Object.prototype.hasOwnProperty.call(options, `${triggerOptionId}`)
  );
};

const reduceQuestionsToNodeTypeMemo = (
  questions: Question[],
  data: DataMemo
): NodeTypeMemo => {
  return questions.reduce(
    (memo: NodeTypeMemo, question) => {
      const { triggerQuestionId } = question;
      if (triggerQuestionId && isConditionalQuestion(question, data)) {
        if (
          Object.prototype.hasOwnProperty.call(
            memo.childNodes,
            `${triggerQuestionId}`
          )
        ) {
          memo.childNodes[triggerQuestionId].push(question);
        } else {
          memo.childNodes[triggerQuestionId] = [question];
        }
      } else {
        if (!question.hasTrigger) {
          memo.rootNodes.push(question);
        }
      }
      return memo;
    },
    {
      rootNodes: [],
      childNodes: {}
    }
  );
};

const triggeredQuestions = (
  question: Question,
  answer: string | string[],
  children?: Question[]
): Question[] => {
  if (!children) {
    return [];
  }
  return children.reduce((memo: Question[], child) => {
    const { logicOperator, triggerOptionId } = child;
    // ignore data type of variables, values should match
    // used only value equality as type differs for different questions
    if (Array.isArray(answer)) {
      if (logicOperator) {
        if (answer.some((value) => value == `${triggerOptionId}`)) {
          memo.push(child);
        }
      } else {
        if (
          answer.length > 0 &&
          answer.every((value) => value != `${triggerOptionId}`)
        ) {
          memo.push(child);
        }
      }
    } else {
      if (logicOperator) {
        if (answer == `${triggerOptionId}`) {
          memo.push(child);
        }
      } else {
        if (answer && answer != `${triggerOptionId}`) {
          memo.push(child);
        }
      }
    }
    return memo;
  }, []);
};

const reduceQuestionsToTrees = (
  questions: Question[],
  answers: QuestionAnswer
): QuestionTree[] => {
  const { rootNodes, childNodes } = reduceQuestionsToNodeTypeMemo(
    questions,
    reduceQuestionsToDataMemo(questions)
  );

  const mapQuestionToTree = (question: Question): QuestionTree => {
    return {
      ...question,
      children: triggeredQuestions(
        question,
        answers[question.id],
        childNodes[question.id]
      )
        .filter((child) => !child.deleted)
        .sort((a, b) => a.sequence - b.sequence)
        .map(mapQuestionToTree)
    };
  };

  return rootNodes
    .filter((node) => !node.deleted)
    .sort((a, b) => a.sequence - b.sequence)
    .map(mapQuestionToTree);
};

const traverseTree = (
  node: QuestionTree,
  callback: (node: Question) => void
): void => {
  const { children, ...question } = node;
  callback(question);
  children.forEach((node) => {
    traverseTree(node, callback);
  });
};

const flattenTrees = (trees: QuestionTree[]): Question[] => {
  const flattenTree = (tree: QuestionTree): Question[] => {
    const questions: Question[] = [];
    traverseTree(tree, (question) => {
      questions.push(question);
    });
    return questions;
  };

  return trees.reduce((memo: Question[], tree) => {
    memo.push(...flattenTree(tree));
    return memo;
  }, []);
};

export const reduceQuestionsToFlattenedTrees = (
  questions: Question[],
  answers: QuestionAnswer
): Question[] => {
  return flattenTrees(reduceQuestionsToTrees(questions, answers));
};

type ValidateFunction = (
  answer: any,
  question: Partial<Question>
) => Promise<string[]>;

export const validateQuestion: ValidateFunction = (answer, question) => {
  const Validation: {
    // eslint-disable-next-line no-unused-vars
    [key in QuestionType]: ValidateFunction;
  } = {
    // Section & Image questions have no answers,
    // so just return empty promises indicating no validation erros
    ImageQuestion: async () => [],
    SectionQuestion: async () => [],
    DateQuestion: validateDateAgeQuestion,
    FileQuestion: validateFileQuestion,
    EmailQuestion: validateEmailQuestion,
    EssayQuestion: validateEssayQuestion,
    NumberQuestion: validateNumberQuestion,
    RegionQuestion: validateRegionQuestion,
    CheckBoxQuestion: validateCheckBoxQuestion,
    DropDownQuestion: validateDropDownQuestion,
    SingleLineQuestion: validateSingleLineQuestion,
    RadioButtonQuestion: validateRadioButtonQuestion,
    "SignupForm::TosQuestion": validateTosQuestion,
    "SignupForm::LoginQuestion": validateLoginQuestion,
    "SignupForm::PasswordQuestion": validatePasswordQuestion,
    "SignupForm::AccountEmailQuestion": validateAccountEmailQuestion,
    EmailOrScreenNameQuestion: validateEmailOrScreenNameQuestion,
    LoginPasswordQuestion: validateLoginPasswordQuestion,
    "Demographics::LocationQuestion": validateLocationQuestion
  };

  return Validation[question.type].call(undefined, answer, question);
};
